import { Form, Formik, FormikProps } from 'formik';
import { Ref, useEffect, useMemo, useRef } from 'react';
import { AnyObject } from 'yup';
import OnboardFormLayout from '../../components/OnboardFormLayout';
import OptionalTextDivider from '../../components/OptionalTextDivider';
import { useDivideSchemaFields } from '../../hooks/useDivideSchemaFields';
import { BaseFormControls, SlotProps } from '../GenericForm';
import { formValidator, useCRUDContext } from '../context/CRUDContext';
import { EntityIsNotSelected } from './components/EntityIsNotSelected';
import { FieldsArea } from './components/FieldsArea';
import { FormFieldType } from './components/FormFieldType';
import GenericFormField from './components/GenericFormField';
import { SubmitButton } from './components/SubmitButton';
import { fixedForwardRef } from './utils/forwardRefFix';
import { generateEditInitialValues } from './utils/generateEditInitialValues';
import { resetFormUsingKey } from './utils/resetFormUsingKey';
import { useFormControllerRefSetup } from './utils/useFormControllerRefSetup';

function UpdateFormInner<T extends AnyObject>(
  props: SlotProps<T>,
  ref?: Ref<BaseFormControls | null>
) {
  const crud = useCRUDContext();
  const formikRef = useRef<FormikProps<Partial<T>> | null>(null);
  useFormControllerRefSetup(ref, formikRef);

  const initialValues = useMemo(
    () =>
      generateEditInitialValues(
        crud.currEntity,
        props.fieldsConfig
      ) as Partial<T>,
    [crud.currEntity, props.fieldsConfig]
  );

  // Reset form when entity changes (except while loading to avoid old data flash)
  useEffect(() => {
    if (
      formikRef.current &&
      crud.currEntity !== null &&
      formikRef.current.isSubmitting === false
    ) {
      formikRef.current.resetForm({ values: initialValues });
    }
  }, [crud.currEntity, initialValues, formikRef]);

  const { requiredFields, optionalFields } = useDivideSchemaFields(
    crud.yupSchemaOut
  );

  const renderFormFields = (fieldNames: string[]) =>
    fieldNames
      .filter((key) => !props?.hiddenFields?.includes(key))
      .map((key) => {
        const fieldProps = props.fieldsConfig?.[key] || {
          type: FormFieldType.TextField,
          label: key,
          initialValue: '',
        };
        return <GenericFormField key={key} name={key} fieldMeta={fieldProps} />;
      });

  if (crud.currEntity === null) {
    return (
      <EntityIsNotSelected message={props.textOverrides?.entityIsNotSelected} />
    );
  }

  const formKey = resetFormUsingKey(crud.currEntity);

  return (
    <Formik
      key={formKey}
      innerRef={formikRef}
      initialValues={initialValues}
      validate={(values) =>
        formValidator({
          value: values,
          schema: crud.yupSchemaOut,
          extraValidation: crud.extraValidation,
        })
      }
      onSubmit={async (value, helpers) =>
        crud.formSubmitHandler({
          helpers,
          operationCallback: async () => {
            return await crud.handleUpdate(value);
          },
        })
      }
    >
      {(formikProps) => (
        <Form
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
          onSubmit={formikProps.handleSubmit}
          onReset={formikProps.handleReset}
        >
          <OnboardFormLayout
            onSubmit={formikProps.handleSubmit}
            formikErrors={formikProps.errors}
          >
            {/* Here we can also do ordering by optional meta to set required fields first */}
            <FieldsArea {...props.gridConfig}>
              {requiredFields.length > 0 && renderFormFields(requiredFields)}
            </FieldsArea>
            {optionalFields.length > 0 && <OptionalTextDivider />}
            <FieldsArea {...props.gridConfig}>
              {optionalFields.length > 0 && renderFormFields(optionalFields)}
            </FieldsArea>

            {props.SubmitButton?.renderFn ? (
              props.SubmitButton.renderFn({
                formikProps: formikProps,
                config: props.SubmitButton?.config,
              })
            ) : (
              <SubmitButton
                formikProps={formikProps}
                config={props.SubmitButton?.config}
              />
            )}
          </OnboardFormLayout>
        </Form>
      )}
    </Formik>
  );
}

export const UpdateForm = fixedForwardRef(UpdateFormInner);
